<!--
  AudioTrackPlayer.vue
  Plays a recording made of one or more audio tracks, laid out like an editor's timeline: a time ruler, one lane
  per track with its clip placed at its offset, and one playhead across them all. Press or drag anywhere on the
  timeline to scrub; the clips play together in sync. A video visit is one track per participant; an in-person
  scribe visit is a single track (no track headers then).

  Each track brings its own `load({ signal, onProgress(received, total) })` that resolves with the file as a Blob,
  so authentication and endpoints stay in api/ and the player works with any audio. Files are prepared by
  webmAudio.js (length, a seekable copy, the waveform). The tracks are read once — key the player to swap them.

  Playback: one detached <audio> per lane, driven by a clock ticked on an interval (animation frames stop in a
  background tab, and staff listen while typing in the EHR). Lanes start and stop as the playhead crosses their
  clip; small drift is evened out with a slight playback-rate nudge, large drift with a jump.
-->
<template>
  <div class="audio-track-player">
    <div class="atp-body">
      <!-- Who each lane is — only worth a column when there's more than one -->
      <div v-if="showHeaders" class="atp-headers">
        <div class="atp-ruler-spacer"></div>
        <div v-for="lane in lanes" :key="lane.id" class="atp-header">
          <div class="atp-header-name" :title="lane.name">{{ lane.name }}</div>
          <div v-if="lane.detail" class="atp-header-detail">{{ lane.detail }}</div>
        </div>
      </div>

      <!-- Ruler + lanes under one playhead; the whole timeline scrubs and takes the keyboard -->
      <div ref="timeline" class="atp-timeline" :class="{ 'atp-timeline--seekable': duration > 0 }" role="slider"
        tabindex="0" aria-label="Playback position" aria-valuemin="0" :aria-valuemax="Math.round(duration)"
        :aria-valuenow="Math.round(position)" :aria-valuetext="valueText" @pointerdown="onPointerDown"
        @pointermove="onPointerMove" @pointerup="onPointerUp" @pointercancel="onPointerCancel"
        @pointerleave="hoverTime = null" @keydown="onKeydown">
        <div class="atp-ruler">
          <div v-for="tick in rulerTicks" :key="tick.time" class="atp-tick"
            :class="{ 'atp-tick--major': tick.major }" :style="{ left: `${tick.left}%` }">
            <span v-if="tick.label !== null" class="atp-tick-label">{{ tick.label }}</span>
          </div>
          <span v-if="hoverTime !== null && !dragging" class="atp-hover-label" :style="{ left: percentOf(hoverTime) }">
            {{ formatClock(hoverTime) }}
          </span>
        </div>

        <div class="atp-lanes">
          <div v-if="allFailed" class="atp-lane">
            <span class="atp-lane-status atp-lane-status--error">{{ RECORDING_ERROR_TEXT }}</span>
          </div>
          <template v-else>
            <div v-for="lane in lanes" :key="lane.id" class="atp-lane" :data-status="lane.status">
              <div v-if="lane.status === LANE_STATUS.READY" class="atp-clip" :style="clipStyle(lane)"
                :title="lane.waveformUnavailable ? WAVEFORM_UNAVAILABLE_TEXT : null">
                <AudioWaveform v-if="lane.peaks" class="atp-clip-wave" :peaks="lane.peaks"
                  :peaksPerSecond="PEAKS_PER_SECOND" :start="0" :end="lane.duration"
                  :playedUntil="position - lane.offset" />
                <div v-else-if="lane.waveformUnavailable" class="atp-clip-flat"></div>
              </div>
              <span v-else class="atp-lane-status" :class="{ 'atp-lane-status--error': lane.status === LANE_STATUS.ERROR }">
                {{ laneStatusText(lane) }}
              </span>
            </div>
          </template>
        </div>

        <div v-if="hoverTime !== null && !dragging" class="atp-hover-line" :style="{ left: percentOf(hoverTime) }"></div>
        <div v-if="duration > 0" class="atp-playhead" :style="{ left: percentOf(position) }"></div>
      </div>
    </div>

    <div class="atp-transport">
      <BaseButton size="small" :icon="playing ? 'pause' : 'play'" :disabled="!canPlay"
        :aria-label="playing ? 'Pause' : 'Play'" @click="togglePlay" />
      <span class="atp-time">{{ timeText }}</span>
    </div>
  </div>
</template>

<script>
import BaseButton from '../buttons/BaseButton.vue'
import AudioWaveform from './AudioWaveform.vue'
import { prepareAudio, PEAKS_PER_SECOND } from './webmAudio.js'

const LANE_STATUS = Object.freeze({
  LOADING: 'loading',
  PREPARING: 'preparing',
  READY: 'ready',
  ERROR: 'error'
})

const LOAD_ERROR_TEXT = 'Couldn\'t load this track'
const PLAYBACK_ERROR_TEXT = 'This browser can\'t play this recording'
const RECORDING_ERROR_TEXT = 'Couldn\'t load the recording'
const WAVEFORM_UNAVAILABLE_TEXT = 'Waveform not available in this browser'

// Clock tick — fine enough for the playhead and drift checks, and unthrottled in a tab that's playing audio
const TICK_MS = 50
// A lane within this of the clock is left alone
const SEEK_TOLERANCE_S = 0.05
// Drift under this is ignored; up to MAX_DRIFT_S it's evened out by nudging the rate; past it the lane jumps
const DRIFT_DEADBAND_S = 0.03
const MAX_DRIFT_S = 0.75
const MAX_RATE_NUDGE = 0.05
// How long play/seek waits for the lanes under the playhead to finish seeking before going anyway
const BUFFER_HOLD_MS = 3000
// HTMLMediaElement.HAVE_FUTURE_DATA (not every environment exposes the constant)
const HAVE_FUTURE_DATA = 3
const ARROW_STEP_S = 5
const PAGE_STEP_S = 30

// [major tick spacing in seconds, minor ticks per major] — the first whose labels sit far enough apart wins
const RULER_STEPS = [
  [1, 5], [2, 4], [5, 5], [10, 5], [15, 3], [30, 6], [60, 6], [120, 4],
  [300, 5], [600, 5], [900, 3], [1800, 6], [3600, 6]
]
const MIN_LABEL_SPACING_PX = 72
// Room a ruler label takes to the right of its tick (its 4px offset + ~6.5px a character at 11px): one that would run
// past the right edge is left off — its tick stays a major one
const LABEL_OFFSET_PX = 4
const LABEL_CHAR_PX = 6.5

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value))
}

export default {
  name: 'AudioTrackPlayer',
  components: { BaseButton, AudioWaveform },
  props: {
    // [{ id, name, detail, offset (seconds on the shared timeline), load({ signal, onProgress }) → Promise<Blob> }]
    tracks: { type: Array, required: true }
  },
  data() {
    if (!Array.isArray(this.tracks) || this.tracks.length === 0) {
      throw Error('AudioTrackPlayer: tracks must be a non-empty array')
    }
    const ids = new Set()
    const lanes = this.tracks.map((track, index) => {
      if (!track || track.id === undefined || track.id === null) throw Error(`AudioTrackPlayer: track ${index} has no id`)
      if (ids.has(track.id)) throw Error(`AudioTrackPlayer: duplicate track id ${track.id}`)
      ids.add(track.id)
      if (typeof track.name !== 'string') throw Error(`AudioTrackPlayer: track ${index} has no name`)
      if (typeof track.offset !== 'number' || !Number.isFinite(track.offset) || track.offset < 0) {
        throw Error(`AudioTrackPlayer: track ${index} has an invalid offset`)
      }
      if (typeof track.load !== 'function') throw Error(`AudioTrackPlayer: track ${index} has no load()`)
      return {
        id: track.id,
        name: track.name,
        detail: track.detail || null,
        offset: track.offset,
        status: LANE_STATUS.LOADING,
        received: 0,
        total: null,
        duration: null,
        peaks: null,
        waveformUnavailable: false,
        error: null
      }
    })
    return {
      LANE_STATUS,
      PEAKS_PER_SECOND,
      RECORDING_ERROR_TEXT,
      WAVEFORM_UNAVAILABLE_TEXT,
      lanes,
      // Seconds on the shared timeline
      position: 0,
      playing: false,
      dragging: false,
      hoverTime: null,
      timelineWidth: 0
    }
  },
  computed: {
    readyLanes() {
      return this.lanes.filter(lane => lane.status === LANE_STATUS.READY)
    },
    // Every lane has finished loading one way or the other
    settled() {
      return this.lanes.every(lane => lane.status === LANE_STATUS.READY || lane.status === LANE_STATUS.ERROR)
    },
    allFailed() {
      return this.lanes.every(lane => lane.status === LANE_STATUS.ERROR)
    },
    canPlay() {
      return this.settled && this.readyLanes.length > 0 && this.duration > 0
    },
    // The timeline ends where the last clip does
    duration() {
      return this.readyLanes.reduce((end, lane) => Math.max(end, lane.offset + lane.duration), 0)
    },
    showHeaders() {
      return this.lanes.length > 1
    },
    showHours() {
      return this.duration >= 3600
    },
    timeText() {
      return `${this.formatClock(this.position)} / ${this.formatClock(this.duration)}`
    },
    valueText() {
      return `${this.formatClock(this.position)} of ${this.formatClock(this.duration)}`
    },
    // Minor ticks between labelled major ones, spaced so labels never crowd: 30 minutes on ~740 px labels every 5:00
    rulerTicks() {
      if (!(this.duration > 0) || !(this.timelineWidth > 0)) return []
      const pixelsPerSecond = this.timelineWidth / this.duration
      const [major, minorsPerMajor] = RULER_STEPS.find(([seconds]) => seconds * pixelsPerSecond >= MIN_LABEL_SPACING_PX)
        || RULER_STEPS[RULER_STEPS.length - 1]
      const minor = major / minorsPerMajor
      const ticks = []
      for (let index = 0; index * minor <= this.duration; index++) {
        const time = index * minor
        const major = index % minorsPerMajor === 0
        const label = major ? this.formatClock(time) : null
        const fits = label !== null
          && time * pixelsPerSecond + LABEL_OFFSET_PX + label.length * LABEL_CHAR_PX <= this.timelineWidth
        ticks.push({ time, left: time / this.duration * 100, major, label: fits ? label : null })
      }
      return ticks
    }
  },
  created() {
    // Playback machinery — not reactive
    this.audios = new Map()
    this.urls = []
    this.controller = new AbortController()
    this.timer = null
    this.anchor = null
    this.buffering = false
    this.bufferingSince = 0
    this.resumeAfterDrag = false
    this.resizeObserver = null
  },
  mounted() {
    this.lanes.forEach((lane, index) => this.loadLane(lane, this.tracks[index]))
    const timeline = this.$refs.timeline
    if (typeof ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(entries => {
        this.timelineWidth = entries[0].contentRect.width
      })
      this.resizeObserver.observe(timeline)
    }
    this.timelineWidth = timeline.getBoundingClientRect().width
  },
  beforeUnmount() {
    this.controller.abort()
    this.playing = false
    clearInterval(this.timer)
    this.timer = null
    this.audios.forEach(audio => {
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
    })
    this.audios.clear()
    this.urls.forEach(url => URL.revokeObjectURL(url))
    this.urls = []
    if (this.resizeObserver) this.resizeObserver.disconnect()
  },
  methods: {
    // Download → prepare → playable; the waveform then fills in while the lane can already be played
    async loadLane(lane, track) {
      const { signal } = this.controller
      let prepared
      try {
        const blob = await track.load({
          signal,
          onProgress: (received, total) => {
            lane.received = received
            lane.total = total > 0 ? total : null
          }
        })
        if (!(blob instanceof Blob)) throw Error('AudioTrackPlayer: load() must resolve with a Blob')
        lane.status = LANE_STATUS.PREPARING
        prepared = await prepareAudio(blob, { signal })
      } catch (err) {
        if (signal.aborted) return
        console.error('Failed to load a recording track', err.message)
        lane.status = LANE_STATUS.ERROR
        lane.error = LOAD_ERROR_TEXT
        return
      }
      if (signal.aborted) return

      this.attachAudio(lane, prepared.playable)
      lane.duration = prepared.duration
      lane.status = LANE_STATUS.READY

      try {
        const peaks = await prepared.decodePeaks({ onProgress: partial => { lane.peaks = partial } })
        if (signal.aborted) return
        lane.peaks = peaks
        lane.waveformUnavailable = !peaks
      } catch (err) {
        if (signal.aborted) return
        console.error('Failed to draw a recording track', err.message)
        lane.peaks = null
        lane.waveformUnavailable = true
      }
    },
    attachAudio(lane, playable) {
      const audio = document.createElement('audio')
      audio.preload = 'auto'
      const url = URL.createObjectURL(playable)
      this.urls.push(url)
      audio.addEventListener('error', () => this.failLane(lane))
      audio.src = url
      this.audios.set(lane.id, audio)
    },
    // The element can't play what it was given (a format this browser lacks)
    failLane(lane) {
      if (this.controller.signal.aborted) return
      const audio = this.audios.get(lane.id)
      if (audio) audio.pause()
      this.audios.delete(lane.id)
      lane.status = LANE_STATUS.ERROR
      lane.error = PLAYBACK_ERROR_TEXT
      if (this.playing && this.readyLanes.length === 0) this.pause()
    },

    play() {
      if (!this.canPlay || this.playing) return
      if (this.position >= this.duration) this.position = 0
      this.playing = true
      this.startFrom()
      this.timer = setInterval(() => this.tick(performance.now()), TICK_MS)
    },
    pause() {
      this.playing = false
      this.buffering = false
      clearInterval(this.timer)
      this.timer = null
      this.audios.forEach(audio => audio.pause())
    },
    togglePlay() {
      if (this.playing) this.pause()
      else this.play()
    },
    // While paused only the playhead moves; play() lines the lanes up from wherever it is
    seek(time) {
      this.position = clamp(time, 0, this.duration)
      if (this.playing) this.startFrom()
    },
    // Put every lane under the playhead at the right spot, then hold the clock until they've finished seeking
    startFrom() {
      for (const lane of this.readyLanes) {
        const audio = this.audios.get(lane.id)
        if (!audio) continue
        audio.pause()
        const laneTime = this.position - lane.offset
        if (laneTime >= 0 && laneTime < lane.duration && Math.abs(audio.currentTime - laneTime) > SEEK_TOLERANCE_S) {
          audio.currentTime = laneTime
        }
      }
      this.buffering = true
      this.bufferingSince = performance.now()
    },
    isUnderPlayhead(lane) {
      const laneTime = this.position - lane.offset
      return laneTime >= 0 && laneTime < lane.duration
    },
    tick(now) {
      if (!this.playing) return
      if (this.buffering) {
        const waiting = this.readyLanes.some(lane => {
          const audio = this.audios.get(lane.id)
          return audio && this.isUnderPlayhead(lane) && (audio.seeking || audio.readyState < HAVE_FUTURE_DATA)
        })
        if (waiting && now - this.bufferingSince < BUFFER_HOLD_MS) return
        this.buffering = false
        this.anchor = { position: this.position, at: now }
      }

      this.position = Math.min(this.duration, this.anchor.position + (now - this.anchor.at) / 1000)
      if (this.position >= this.duration) {
        this.pause()
        return
      }

      for (const lane of this.readyLanes) {
        const audio = this.audios.get(lane.id)
        if (!audio) continue
        const laneTime = this.position - lane.offset
        if (laneTime < 0 || laneTime >= lane.duration || audio.ended) {
          if (!audio.paused) audio.pause()
          continue
        }
        // > 0: the lane is behind the clock
        const drift = laneTime - audio.currentTime
        if (audio.paused) {
          // The playhead just entered this clip
          if (Math.abs(drift) > SEEK_TOLERANCE_S) audio.currentTime = laneTime
          if (audio.playbackRate !== 1) audio.playbackRate = 1
          this.playAudio(audio)
          continue
        }
        if (Math.abs(drift) > MAX_DRIFT_S) {
          audio.currentTime = laneTime
          continue
        }
        const rate = Math.abs(drift) < DRIFT_DEADBAND_S
          ? 1
          : Math.round((1 + clamp(drift, -MAX_RATE_NUDGE, MAX_RATE_NUDGE)) * 100) / 100
        if (audio.playbackRate !== rate) audio.playbackRate = rate
      }
    },
    playAudio(audio) {
      const playing = audio.play()
      if (playing && typeof playing.catch === 'function') {
        // Refused without a user gesture: stop the clock rather than run silently. An AbortError (a pause or seek
        // overtook it) is harmless.
        playing.catch(err => {
          if (err && err.name === 'NotAllowedError') this.pause()
        })
      }
    },

    // Seconds on the timeline under a pointer event
    timeAt(event) {
      const rect = this.$refs.timeline.getBoundingClientRect()
      if (!(rect.width > 0)) return 0
      return clamp((event.clientX - rect.left) / rect.width, 0, 1) * this.duration
    },
    onPointerDown(event) {
      if (!(this.duration > 0) || (event.button !== undefined && event.button !== 0)) return
      const timeline = this.$refs.timeline
      if (timeline.setPointerCapture && event.pointerId !== undefined) timeline.setPointerCapture(event.pointerId)
      this.dragging = true
      this.resumeAfterDrag = this.playing
      if (this.playing) this.pause()
      this.position = this.timeAt(event)
      timeline.focus()
    },
    // Dragging moves only the playhead; the lanes seek once, on release
    onPointerMove(event) {
      if (!(this.duration > 0)) return
      const time = this.timeAt(event)
      if (this.dragging) this.position = time
      else this.hoverTime = time
    },
    onPointerUp(event) {
      if (!this.dragging) return
      const timeline = this.$refs.timeline
      if (timeline.releasePointerCapture && event.pointerId !== undefined) timeline.releasePointerCapture(event.pointerId)
      this.dragging = false
      this.seek(this.timeAt(event))
      if (this.resumeAfterDrag) this.play()
    },
    onPointerCancel() {
      if (!this.dragging) return
      this.dragging = false
      this.seek(this.position)
      if (this.resumeAfterDrag) this.play()
    },
    onKeydown(event) {
      if (!(this.duration > 0)) return
      const step = event.shiftKey ? PAGE_STEP_S : ARROW_STEP_S
      switch (event.key) {
        case ' ':
        case 'Spacebar':
          this.togglePlay()
          break
        case 'ArrowLeft':
          this.seek(this.position - step)
          break
        case 'ArrowRight':
          this.seek(this.position + step)
          break
        case 'PageDown':
          this.seek(this.position - PAGE_STEP_S)
          break
        case 'PageUp':
          this.seek(this.position + PAGE_STEP_S)
          break
        case 'Home':
          this.seek(0)
          break
        case 'End':
          this.seek(this.duration)
          break
        default:
          return
      }
      event.preventDefault()
    },

    percentOf(time) {
      if (!(this.duration > 0)) return '0%'
      return `${clamp(time / this.duration, 0, 1) * 100}%`
    },
    clipStyle(lane) {
      return {
        left: this.percentOf(lane.offset),
        width: this.duration > 0 ? `${lane.duration / this.duration * 100}%` : '0%'
      }
    },
    laneStatusText(lane) {
      if (lane.status === LANE_STATUS.ERROR) return lane.error
      if (lane.status === LANE_STATUS.PREPARING) return 'Preparing'
      if (lane.total) return `Downloading · ${Math.min(100, Math.floor(lane.received / lane.total * 100))}%`
      if (lane.received) return `Downloading · ${(lane.received / 1e6).toFixed(1)} MB`
      return 'Downloading'
    },
    // m:ss, or h:mm:ss throughout once the timeline reaches an hour
    formatClock(seconds) {
      const total = Math.max(0, Math.floor(seconds))
      const secs = String(total % 60).padStart(2, '0')
      if (this.showHours) {
        const mins = String(Math.floor(total / 60) % 60).padStart(2, '0')
        return `${Math.floor(total / 3600)}:${mins}:${secs}`
      }
      return `${Math.floor(total / 60)}:${secs}`
    }
  }
}
</script>

<style scoped>
.audio-track-player {
  border: 1px solid var(--color-border-med);
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  user-select: none;
}

.atp-body {
  display: flex;
}

/* Track headers */
.atp-headers {
  flex: 0 0 160px;
  min-width: 0;
  border-right: 1px solid var(--color-border-med);
  background: var(--color-fill-light);
}

.atp-ruler-spacer {
  height: 24px;
  box-sizing: border-box;
  border-bottom: 1px solid var(--color-border-med);
  background: var(--color-fill-med);
}

.atp-header {
  height: 64px;
  box-sizing: border-box;
  padding: 0 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  border-bottom: 1px solid var(--color-border-light);
}

.atp-header:last-child {
  border-bottom: none;
}

.atp-header-name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-txt-dark);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.atp-header-detail {
  font-size: 0.75rem;
  color: var(--color-txt-light);
}

/* Timeline */
.atp-timeline {
  position: relative;
  flex: 1;
  min-width: 0;
  outline: none;
  touch-action: none;
}

.atp-timeline--seekable {
  cursor: pointer;
}

.atp-timeline:focus-visible {
  box-shadow: inset 0 0 0 2px color-mix(in srgb, var(--color-primary) 40%, transparent);
}

.atp-ruler {
  position: relative;
  height: 24px;
  box-sizing: border-box;
  border-bottom: 1px solid var(--color-border-med);
  background: var(--color-fill-med);
  overflow: hidden;
}

.atp-tick {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
}

.atp-tick::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 1px;
  height: 5px;
  background: var(--color-border-dark);
}

.atp-tick--major::after {
  height: 10px;
  background: var(--color-txt-light);
}

.atp-tick-label {
  position: absolute;
  left: 4px;
  top: 3px;
  font-size: 0.6875rem;
  line-height: 1;
  color: var(--color-txt-light);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.atp-hover-label {
  position: absolute;
  top: 3px;
  transform: translateX(-50%);
  padding: 1px 4px;
  border-radius: 3px;
  background: var(--color-txt-dark);
  color: #fff;
  font-size: 0.6875rem;
  line-height: 1.2;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
  z-index: 2;
}

/* Lanes */
.atp-lane {
  position: relative;
  height: 64px;
  box-sizing: border-box;
  border-bottom: 1px solid var(--color-border-light);
}

.atp-lane:last-child {
  border-bottom: none;
}

.atp-clip {
  position: absolute;
  top: 6px;
  bottom: 6px;
  box-sizing: border-box;
  border: 1px solid color-mix(in srgb, var(--color-primary) 20%, transparent);
  border-radius: 4px;
  background: color-mix(in srgb, var(--color-primary) 6%, #fff);
  overflow: hidden;
}

/* Two classes, so this outranks AudioWaveform's own full-size rule */
.atp-clip .atp-clip-wave {
  position: absolute;
  inset: 4px 0;
  width: auto;
  height: auto;
}

.atp-clip-flat {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  border-top: 1px solid color-mix(in srgb, var(--color-primary) 35%, transparent);
}

.atp-lane-status {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8125rem;
  color: var(--color-txt-light);
  white-space: nowrap;
}

.atp-lane-status--error {
  color: var(--color-status-danger);
}

.atp-playhead {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  margin-left: -1px;
  background: var(--color-txt-dark);
  pointer-events: none;
  z-index: 1;
}

.atp-hover-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--color-border-dark);
  pointer-events: none;
}

/* Transport */
.atp-transport {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-top: 1px solid var(--color-border-med);
  background: var(--color-fill-light);
}

.atp-time {
  font-size: 0.875rem;
  color: var(--color-txt-med);
  font-variant-numeric: tabular-nums;
}
</style>
